import type { PETComposition, SpecificationListItem } from 'api';
import { AIModelChoices } from 'api';
import { useTranslation } from 'i18n';
import { Stack } from 'ui';

import { buildPETCompositionItems } from '../../utils/buildPETCompositionItems/buildPETCompositionItems';
import { formatDirtScore } from '../../utils/formatDirtScore/formatDirtScore';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';

type Props = {
  composition: PETComposition;
  instructions?: SpecificationListItem[];
  modelName?: AIModelChoices;
};

export const PETCompositionBreakdown = ({ composition, instructions, modelName }: Props) => {
  const { t } = useTranslation('features');
  const { targetPetSection, nonTargetSection } = buildPETCompositionItems(composition, instructions);
  const shouldShowCleanliness = modelName !== AIModelChoices.pet_envases;

  return (
    <Stack
      data-testid="pet-composition-breakdown"
      sx={{
        gap: 2,
      }}
    >
      <div>
        {targetPetSection.items.map((item, index) => (
          <MaterialBreakdownItem
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value}
            highlight={item.highlight}
            variant={item.variant}
            dataTestId={item.dataTestId}
          />
        ))}
      </div>
      <div>
        {nonTargetSection.items.map((item, index) => (
          <MaterialBreakdownItem
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value}
            highlight={item.highlight}
            variant={item.variant}
            dataTestId={item.dataTestId}
          />
        ))}
      </div>
      {shouldShowCleanliness ? (
        <div>
          <MaterialBreakdownItem
            variant="bold"
            label={t('breakdowns.cleanliness.label')}
            tooltipContent={t('breakdowns.cleanliness.tooltip')}
            value={formatDirtScore(composition.dirt_score)}
          />
        </div>
      ) : null}
    </Stack>
  );
};
